import { useState, useEffect, useCallback } from 'react';
import api, { cash as cashApi, sites as sitesApi } from '../utils/api';
import { useAuth } from '../utils/auth';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Trash2, RotateCcw, History, CheckCircle, Edit2, Save, X } from 'lucide-react';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import ClosureFlow, { SafeHistory } from '../components/ClosureFlow';

const BILLS = [
  { k:'bill_100', fk:'fond_bill_100', label:'100 €', val:100, color:'#22d3a0' },
  { k:'bill_50',  fk:'fond_bill_50',  label:'50 €',  val:50,  color:'#22d3a0' },
  { k:'bill_20',  fk:'fond_bill_20',  label:'20 €',  val:20,  color:'#a387fd' },
  { k:'bill_10',  fk:'fond_bill_10',  label:'10 €',  val:10,  color:'#a387fd' },
  { k:'bill_5',   fk:'fond_bill_5',   label:'5 €',   val:5,   color:'#a387fd' },
];
const COINS = [
  { k:'coin_200', fk:'fond_coin_200', label:'2 €',    val:2    },
  { k:'coin_100', fk:'fond_coin_100', label:'1 €',    val:1    },
  { k:'coin_50',  fk:'fond_coin_50',  label:'0,50 €', val:0.5  },
  { k:'coin_20',  fk:'fond_coin_20',  label:'0,20 €', val:0.2  },
  { k:'coin_10',  fk:'fond_coin_10',  label:'0,10 €', val:0.1  },
  { k:'coin_5',   fk:'fond_coin_5',   label:'0,05 €', val:0.05 },
];
const ALL = [...BILLS, ...COINS];
const EMPTY = Object.fromEntries(ALL.map(d => [d.k, '']));
function fmt(n) { return parseFloat(n || 0).toFixed(2) + ' €'; }

function DenomRow({ item, value, fond, onChange }) {
  const qty = parseInt(value) || 0;
  const fondQty = parseInt(fond) || 0;
  const diff = qty - fondQty;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8,
      padding:'5px 10px', borderRadius:7, marginBottom:3,
      background: qty > 0 ? 'var(--bg-elevated)' : 'transparent' }}>
      <span style={{ fontWeight:700, minWidth:46, fontSize:14, color: item.color || 'var(--text-2)' }}>{item.label}</span>
      <span style={{ color:'var(--text-3)', fontSize:11 }}>×</span>
      <input type="number" min={0} value={value}
        onChange={e => onChange(e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value)||0))}
        onFocus={e => e.target.select()}
        style={{ width:46, textAlign:'center', background:'var(--bg-base)',
          border:`1px solid ${qty > 0 ? 'rgba(124,92,252,0.3)' : 'var(--border)'}`,
          borderRadius:6, padding:'4px 2px', color:'var(--text-1)',
          fontSize:14, fontWeight:700, outline:'none', fontFamily:'monospace' }}/>
      {fondQty > 0 && <span style={{ fontSize:11, color:'var(--text-3)', minWidth:28 }}>/{fondQty}</span>}
      <span style={{ marginLeft:'auto', fontSize:13, fontWeight:600,
        color: qty > 0 ? 'var(--teal)' : 'var(--text-3)', minWidth:60, textAlign:'right' }}>
        {qty > 0 ? fmt(qty * item.val) : '—'}
      </span>
      {qty > 0 && fondQty > 0 && diff !== 0 && (
        <span style={{ fontSize:11, minWidth:36, textAlign:'right',
          color: diff > 0 ? 'var(--teal)' : 'var(--red)', fontWeight:600 }}>
          {diff > 0 ? `+${diff}` : diff}
        </span>
      )}
    </div>
  );
}

function CountTab({ sites }) {
  const { can } = useAuth();
  const isMobile = useIsMobile();
  const [counts, setCounts]           = useState(EMPTY);
  const [fond, setFond]               = useState({});
  const [notes, setNotes]             = useState('');
  const [siteId, setSiteId]           = useState(sites[0]?.id || '');
  const [cashToday, setCashToday]     = useState(0);
  const [manualToday, setManualToday] = useState([]);
  const [saving, setSaving]           = useState(false);
  const [lastResult, setResult]       = useState(null);

  useEffect(() => {
    if (!siteId) return;
    cashApi.getFond(siteId).then(r => setFond(r.data.fond || {})).catch(() => {});
  }, [siteId]);

  useEffect(() => {
    if (!siteId) return;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    cashApi.today(today, siteId).then(r => setCashToday(r.data.total || 0)).catch(() => {});
    api.get('/cash/manual', { params:{ siteId, date:today } }).then(r => setManualToday(r.data.entries || [])).catch(() => {});
  }, [siteId]);

  const totalBills    = BILLS.reduce((s, d) => s + (parseInt(counts[d.k])||0) * d.val, 0);
  const totalCoins    = COINS.reduce((s, d) => s + (parseInt(counts[d.k])||0) * d.val, 0);
  const totalCounted  = totalBills + totalCoins;
  const fondBills     = BILLS.reduce((s, d) => s + (parseInt(fond[d.fk])||0) * d.val, 0);
  const fondCoins     = COINS.reduce((s, d) => s + (parseInt(fond[d.fk])||0) * d.val, 0);
  const totalFond     = fondBills + fondCoins;
  const totalManual   = manualToday.reduce((s,e) => s + parseFloat(e.amount||0), 0);
  const totalExpected = totalFond + cashToday + totalManual;
  const ecart         = totalCounted - totalExpected;
  const ecartOk       = Math.abs(ecart) < 0.01;
  const hasEntries    = Object.values(counts).some(v => parseInt(v) > 0);

  const reset = () => { setCounts(EMPTY); setNotes(''); setResult(null); };

  const validate = async () => {
    setSaving(true);
    try {
      const mapped = {
        bill500:0, bill200:0,
        bill100: parseInt(counts.bill_100)||0, bill50: parseInt(counts.bill_50)||0,
        bill20:  parseInt(counts.bill_20)||0,  bill10: parseInt(counts.bill_10)||0,
        bill5:   parseInt(counts.bill_5)||0,
        coin200: parseInt(counts.coin_200)||0, coin100: parseInt(counts.coin_100)||0,
        coin50:  parseInt(counts.coin_50)||0,  coin20: parseInt(counts.coin_20)||0,
        coin10:  parseInt(counts.coin_10)||0,  coin5:  parseInt(counts.coin_5)||0,
        coin2:0, coin1:0, notes, siteId: siteId || null,
      };
      const r = await cashApi.count(mapped);
      setResult(r.data);
      toast.success('Comptage validé');
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap:20, maxWidth:900 }}>
      <div>
        {sites.length > 1 && (
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
            {sites.map(s => (
              <button key={s.id} type="button" onClick={() => setSiteId(s.id)}
                style={{ padding:'6px 16px', borderRadius:8, border:`1px solid ${siteId===s.id?(s.color||'var(--accent)'):'var(--border)'}`,
                  background: siteId===s.id ? `${s.color||'var(--accent)'}22` : 'var(--bg-elevated)',
                  color: siteId===s.id ? (s.color||'var(--accent-light)') : 'var(--text-2)',
                  cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight: siteId===s.id ? 700 : 400 }}>
                {s.name}
              </button>
            ))}
          </div>
        )}
        <div className="card" style={{ marginBottom:12, padding:'14px 16px' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>💵 Billets</div>
          {BILLS.map(d => <DenomRow key={d.k} item={d} value={counts[d.k]} fond={fond[d.fk]} onChange={v => setCounts(p=>({...p,[d.k]:v}))}/>)}
          <div style={{ textAlign:'right', fontWeight:700, color:'var(--teal)', borderTop:'1px solid var(--border)', paddingTop:6, marginTop:4, fontSize:13 }}>
            {fmt(totalBills)}
            {fondBills > 0 && <span style={{ fontSize:11, marginLeft:8, color: Math.abs(totalBills-fondBills)<0.01?'var(--teal)':totalBills>fondBills?'var(--teal)':'var(--red)' }}>(écart : {(totalBills-fondBills)>=0?'+':''}{(totalBills-fondBills).toFixed(2)} €)</span>}
          </div>
        </div>
        <div className="card" style={{ padding:'14px 16px' }}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>🪙 Pièces</div>
          {COINS.map(d => <DenomRow key={d.k} item={d} value={counts[d.k]} fond={fond[d.fk]} onChange={v => setCounts(p=>({...p,[d.k]:v}))}/>)}
          <div style={{ textAlign:'right', fontWeight:700, color:'var(--teal)', borderTop:'1px solid var(--border)', paddingTop:6, marginTop:4, fontSize:13 }}>
            {fmt(totalCoins)}
            {fondCoins > 0 && <span style={{ fontSize:11, marginLeft:8, color: Math.abs(totalCoins-fondCoins)<0.01?'var(--teal)':totalCoins>fondCoins?'var(--teal)':'var(--red)' }}>(écart : {(totalCoins-fondCoins)>=0?'+':''}{(totalCoins-fondCoins).toFixed(2)} €)</span>}
          </div>
        </div>
      </div>

      <div style={{ position: isMobile ? 'static' : 'sticky', top:20 }}>
        <div className="card" style={{ marginBottom:10, padding:'16px' }}>
          <div style={{ fontWeight:700, fontSize:13, marginBottom:14 }}>Rapprochement{siteId && sites.length>1 ? ` — ${sites.find(s=>s.id===siteId)?.name}` : ''}</div>
          <div style={{ textAlign:'center', marginBottom:16 }}>
            <div style={{ fontSize:11, color:'var(--text-3)', marginBottom:2 }}>Total compté</div>
            <div style={{ fontSize:36, fontWeight:800, color:'var(--teal)', lineHeight:1 }}>{totalCounted.toFixed(2)}</div>
            <div style={{ fontSize:12, color:'var(--text-3)' }}>€</div>
          </div>
          {[['Fond de caisse', totalFond], ['Espèces du jour (EscapeOS)', cashToday], ...(totalManual > 0 ? [['Espèces ext. (4Escape)', totalManual]] : []), ['Total attendu', totalExpected]].map(([label, val], i) => (
            <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0',
              borderTop: i===2 ? '1px solid var(--border)' : 'none', fontSize:12, fontWeight: i===2 ? 700 : 400 }}>
              <span style={{ color: i===2 ? 'var(--text-1)' : 'var(--text-2)' }}>{label}</span>
              <span>{fmt(val)}</span>
            </div>
          ))}
          <div style={{ marginTop:10, padding:'10px 12px', borderRadius:8, textAlign:'center',
            background: ecartOk ? 'rgba(0,210,180,0.1)' : 'rgba(255,77,109,0.1)',
            border:`1px solid ${ecartOk ? 'var(--teal)' : 'var(--red)'}` }}>
            <div style={{ fontSize:10, marginBottom:2, color: ecartOk ? 'var(--teal)' : 'var(--red)' }}>
              {ecartOk ? '✓ Caisse équilibrée' : '⚠ Écart détecté'}
            </div>
            <div style={{ fontSize:24, fontWeight:800, color: ecartOk ? 'var(--teal)' : 'var(--red)' }}>
              {ecart >= 0 ? '+' : ''}{ecart.toFixed(2)} €
            </div>
          </div>
        </div>
        {lastResult && (
          <div style={{ padding:'8px 12px', borderRadius:8, marginBottom:10, fontSize:12,
            background:'rgba(0,210,180,0.08)', border:'1px solid var(--teal)', color:'var(--teal)' }}>
            <CheckCircle size={12} style={{display:'inline',marginRight:6}}/>
            Enregistré — écart : {parseFloat(lastResult.difference||0)>=0?'+':''}{parseFloat(lastResult.difference||0).toFixed(2)} €
          </div>
        )}
        <div className="form-group" style={{ marginBottom:10 }}>
          <label className="form-label">Note</label>
          <input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observation…" style={{ fontSize:13 }}/>
        </div>
        <button className="btn btn-primary w-full" style={{ justifyContent:'center', marginBottom:6 }}
          onClick={validate} disabled={saving || !hasEntries || !can('cash.count')}>
          {saving ? <span className="spinner" style={{width:14,height:14}}/> : '✓ Valider le comptage'}
        </button>
        <button className="btn btn-secondary w-full" style={{ justifyContent:'center' }} onClick={reset}>
          <RotateCcw size={13}/> Réinitialiser
        </button>
      </div>
    </div>
  );
}

// ── Modale détail comptage ────────────────────────────────────
const DETAIL_DENOMS = [
  { k:'bill_100', label:'100 €', val:100, color:'#22d3a0', cat:'Billets' },
  { k:'bill_50',  label:'50 €',  val:50,  color:'#22d3a0', cat:'Billets' },
  { k:'bill_20',  label:'20 €',  val:20,  color:'#a387fd', cat:'Billets' },
  { k:'bill_10',  label:'10 €',  val:10,  color:'#a387fd', cat:'Billets' },
  { k:'bill_5',   label:'5 €',   val:5,   color:'#a387fd', cat:'Billets' },
  { k:'coin_200', label:'2 €',   val:2,   color:null, cat:'Pièces' },
  { k:'coin_100', label:'1 €',   val:1,   color:null, cat:'Pièces' },
  { k:'coin_50',  label:'0,50 €',val:0.5, color:null, cat:'Pièces' },
  { k:'coin_20',  label:'0,20 €',val:0.2, color:null, cat:'Pièces' },
  { k:'coin_10',  label:'0,10 €',val:0.1, color:null, cat:'Pièces' },
  { k:'coin_5',   label:'0,05 €',val:0.05,color:null, cat:'Pièces' },
];

function CountDetailModal({ count, canAdmin, onClose, onDeleted, onUpdated }) {
  const [editNotes, setEditNotes] = useState(count.notes || '');
  const [editing, setEditing]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]   = useState(false);

  const bills = DETAIL_DENOMS.filter(d => d.cat === 'Billets');
  const coins = DETAIL_DENOMS.filter(d => d.cat === 'Pièces');
  const ecart = parseFloat(count.difference || 0);

  const Section = ({ title, emoji, denoms }) => {
    const total = denoms.reduce((s, d) => s + (parseInt(count[d.k])||0) * d.val, 0);
    return (
      <div>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>{emoji} {title}</div>
        {denoms.map(d => {
          const qty = parseInt(count[d.k]) || 0;
          if (!qty) return null;
          return (
            <div key={d.k} style={{ display:'flex', alignItems:'center', gap:10, padding:'4px 10px', borderRadius:7, marginBottom:2, background:'var(--bg-elevated)' }}>
              <span style={{ fontWeight:700, minWidth:46, fontSize:13, color: d.color || 'var(--text-2)' }}>{d.label}</span>
              <span style={{ color:'var(--text-3)', fontSize:11 }}>×</span>
              <span style={{ fontWeight:700, fontSize:13, fontFamily:'monospace' }}>{qty}</span>
              <span style={{ marginLeft:'auto', fontWeight:600, fontSize:12, color:'var(--teal)' }}>{fmt(qty * d.val)}</span>
            </div>
          );
        })}
        <div style={{ textAlign:'right', fontWeight:700, color:'var(--teal)', borderTop:'1px solid var(--border)', paddingTop:4, marginTop:4, fontSize:12 }}>{fmt(total)}</div>
      </div>
    );
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await cashApi.remove(count.id);
      toast.success('Comptage supprimé');
      onDeleted();
      onClose();
    } catch { toast.error('Erreur'); setDeleting(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await cashApi.update(count.id, { notes: editNotes });
      toast.success('Mis à jour');
      onUpdated();
      setEditing(false);
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-title" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>Comptage du {format(new Date(count.count_date), 'dd MMMM yyyy', { locale:fr })}</span>
          {canAdmin && (
            <div style={{ display:'flex', gap:6 }}>
              {!editing ? (
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}><Edit2 size={13}/></button>
              ) : (
                <>
                  <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}><Save size={13}/></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}><X size={13}/></button>
                </>
              )}
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(true)}><Trash2 size={13} color="var(--red)"/></button>
            </div>
          )}
        </div>
        <p style={{ fontSize:13, color:'var(--text-3)', marginTop:-8, marginBottom:16 }}>
          {count.first_name} {count.last_name}
          {count.site_name && <span style={{ marginLeft:8, color:'var(--accent-light)' }}>· {count.site_name}</span>}
        </p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          <Section title="Billets" emoji="💵" denoms={bills}/>
          <Section title="Pièces"  emoji="🪙" denoms={coins}/>
        </div>
        <div style={{ background:'var(--bg-elevated)', borderRadius:10, padding:'12px 14px', marginBottom:12 }}>
          {[['Fond de caisse', count.fond_caisse], ['Espèces du jour', count.cash_today], ['Total compté', count.total_counted], ['Total attendu', count.total_expected]].map(([label, val]) => (
            <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13 }}>
              <span style={{ color:'var(--text-2)' }}>{label}</span>
              <span style={{ fontWeight:600 }}>{fmt(val)}</span>
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderTop:'1px solid var(--border)', marginTop:4 }}>
            <span style={{ fontWeight:700 }}>Écart</span>
            <span style={{ fontWeight:800, fontSize:18, color: Math.abs(ecart)<0.01 ? 'var(--teal)' : 'var(--red)' }}>
              {ecart >= 0 ? '+' : ''}{ecart.toFixed(2)} €
            </span>
          </div>
        </div>
        <div className="form-group" style={{ marginBottom:12 }}>
          <label className="form-label">Note</label>
          {editing ? (
            <input className="input" value={editNotes} onChange={e => setEditNotes(e.target.value)}/>
          ) : (
            <p style={{ fontSize:13, color: count.notes ? 'var(--text-1)' : 'var(--text-3)', margin:0 }}>{count.notes || '—'}</p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Supprimer le comptage"
          message="Supprimer ce comptage ?"
          confirmLabel="Supprimer"
          danger
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

function HistoryTab({ sites }) {
  const [history, setHistory]       = useState([]);
  const [detail, setDetail]         = useState(null);
  const [siteFilter, setSiteFilter] = useState('');
  const { can } = useAuth();

  const load = () => cashApi.history(siteFilter || undefined).then(r => setHistory(r.data.counts || []));
  useEffect(() => { load(); }, [siteFilter]);

  return (
    <>
      {sites.length > 1 && (
        <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
          <button onClick={() => setSiteFilter('')}
            style={{ padding:'5px 14px', borderRadius:8, border:`1px solid ${!siteFilter?'var(--accent)':'var(--border)'}`,
              background: !siteFilter?'rgba(124,92,252,0.12)':'var(--bg-elevated)',
              color: !siteFilter?'var(--accent-light)':'var(--text-2)',
              cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight: !siteFilter?700:400 }}>
            Tous les sites
          </button>
          {sites.map(s => (
            <button key={s.id} onClick={() => setSiteFilter(s.id)}
              style={{ padding:'5px 14px', borderRadius:8, border:`1px solid ${siteFilter===s.id?(s.color||'var(--accent)'):'var(--border)'}`,
                background: siteFilter===s.id?`${s.color||'var(--accent)'}22`:'var(--bg-elevated)',
                color: siteFilter===s.id?(s.color||'var(--accent-light)'):'var(--text-2)',
                cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight: siteFilter===s.id?700:400 }}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="card" style={{ maxWidth:900 }}>
        {history.length === 0 ? (
          <div className="empty-state"><p>Aucun comptage enregistré</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Compteur</th>
                  {sites.length > 1 && <th>Site</th>}
                  <th className="text-right">Fond</th>
                  <th className="text-right">Espèces</th>
                  <th className="text-right" style={{fontSize:11,color:'var(--text-3)'}}>Ext.</th>
                  <th className="text-right">Attendu</th>
                  <th className="text-right">Compté</th>
                  <th className="text-right">Écart</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => {
                  const manualT = parseFloat(h.manual_total||0);
                  const expectedWithManual = parseFloat(h.total_expected||0) + manualT;
                  const ecart = parseFloat(h.total_counted||0) - expectedWithManual;
                  const ecartOk = Math.abs(ecart) < 0.01;
                  return (
                    <tr key={h.id} onClick={() => setDetail(h)} style={{ cursor:'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                      onMouseLeave={e => e.currentTarget.style.background=''}>
                      <td className="text-sm">{format(new Date(h.count_date), 'dd/MM/yyyy', {locale:fr})}</td>
                      <td className="text-sm">{h.first_name} {h.last_name}</td>
                      {sites.length > 1 && <td className="text-sm">{h.site_name || '—'}</td>}
                      <td className="text-right text-muted text-sm">{fmt(h.fond_caisse??0)}</td>
                      <td className="text-right text-muted text-sm">{fmt(h.cash_today??0)}</td>
                      {parseFloat(h.manual_total||0) > 0 && (
                        <td className="text-right text-sm" style={{color:'var(--accent-light)',fontSize:12}}>+{fmt(h.manual_total)}</td>
                      )}
                      <td className="text-right text-sm">{fmt(expectedWithManual)}</td>
                      <td className="text-right bold">{fmt(h.total_counted)}</td>
                      <td className="text-right" style={{ color: ecartOk?'var(--teal)':'var(--red)', fontWeight:700 }}>
                        {ecart >= 0 ? '+' : ''}{ecart.toFixed(2)} €
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {detail && (
        <CountDetailModal count={detail} canAdmin={can('admin')}
          onClose={() => setDetail(null)} onDeleted={load}
          onUpdated={() => { load(); setDetail(null); }}/>
      )}
    </>
  );
}

function ManualEntryTab({ sites }) {
  const [entries, setEntries] = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const getToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  const defaultSite = sites.length > 0 ? sites[0].id : '';
  const [form, setForm] = useState({ amount: '', label: '', date: getToday(), siteId: defaultSite, type: 'in' });

  const load = useCallback(async () => {
    try { const r = await api.get('/cash/manual'); setEntries(r.data.entries || []); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async e => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) === 0) return toast.error('Montant requis');
    if (sites.length > 0 && !form.siteId) return toast.error('Site requis');
    setSaving(true);
    try {
      const amt = parseFloat(form.amount);
      await api.post('/cash/manual', {
        amount: form.type === 'out' ? -Math.abs(amt) : Math.abs(amt),
        label:  form.label || null,
        date:   form.date,
        siteId: form.siteId || null,
      });
      toast.success('Entrée enregistrée');
      setForm(p => ({ ...p, amount: '', label: '' }));
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await api.delete('/cash/manual/' + confirmRemove.id);
      toast.success('Supprimé');
      setConfirmRemove(null);
      load();
    } catch { toast.error('Erreur'); }
    finally { setRemoving(false); }
  };

  const safeDate = (val) => {
    if (!val) return '—';
    try {
      const s = String(val).slice(0,10);
      const [y,m,d] = s.split('-').map(Number);
      return format(new Date(y, m-1, d, 12, 0, 0), 'd MMM yyyy', { locale: fr });
    } catch { return '—'; }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:16 }}>Nouvelle entrée</div>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Type</label>
              <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:'1px solid var(--border)' }}>
                {[['in','Entrée (+)','var(--teal)'],['out','Sortie (−)','var(--red)']].map(([v,l,col]) => (
                  <button key={v} type="button" onClick={() => setForm(p=>({...p,type:v}))}
                    style={{ flex:1, padding:'9px', border:'none', cursor:'pointer', fontFamily:'inherit',
                      fontSize:13, fontWeight:600,
                      background: form.type===v ? (v==='in'?'rgba(0,210,180,0.15)':'rgba(255,77,109,0.15)') : 'var(--bg-elevated)',
                      color: form.type===v ? col : 'var(--text-3)' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Montant (€) *</label>
              <input type="number" step="0.01" min="0" className="input" placeholder="0.00"
                value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))} required/>
            </div>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="input" value={form.date}
                onChange={e=>setForm(p=>({...p,date:e.target.value}))}/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex:2 }}>
              <label className="form-label">Libellé</label>
              <input className="input" placeholder="Ex: Ventes 4Escape, Bar…" value={form.label}
                onChange={e=>setForm(p=>({...p,label:e.target.value}))}/>
            </div>
            {sites.length > 0 && (
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Site</label>
                <select className="input" value={form.siteId} required
                  onChange={e=>setForm(p=>({...p,siteId:e.target.value}))}>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            <Plus size={14}/> Enregistrer
          </button>
        </form>
      </div>
      {entries.length === 0 ? (
        <div className="empty-state card"><p>Aucune entrée manuelle</p></div>
      ) : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Libellé</th><th>Type</th><th style={{textAlign:'right'}}>Montant</th><th></th></tr>
              </thead>
              <tbody>
                {entries.map(e => {
                  const amt = parseFloat(e.amount || 0);
                  return (
                    <tr key={e.id}>
                      <td>{safeDate(e.entry_date_str || e.entry_date)}</td>
                      <td style={{ color:'var(--text-2)' }}>{e.label || '—'}</td>
                      <td>
                        <span className={`badge ${amt >= 0 ? 'badge-green' : 'badge-red'}`}>
                          {amt >= 0 ? 'Entrée' : 'Sortie'}
                        </span>
                      </td>
                      <td style={{ textAlign:'right', fontWeight:700, color: amt>=0?'var(--teal)':'var(--red)' }}>
                        {amt >= 0 ? '+' : ''}{amt.toFixed(2)} €
                      </td>
                      <td style={{ textAlign:'right' }}>
                        <button className="btn btn-ghost btn-sm" style={{ padding:'3px 6px' }}
                          onClick={() => setConfirmRemove(e)}>
                          <Trash2 size={13} color="var(--red)"/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {confirmRemove && (
        <ConfirmModal
          title="Supprimer l'entrée"
          message="Supprimer cette entrée ?"
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

export default function CashPage() {
  const { can } = useAuth();
  const [tab,          setTab]         = useState('count');
  const [sites,        setSites]       = useState([]);
  const [closureOpen,  setClosureOpen] = useState(false);

  useEffect(() => { sitesApi.list().then(r => setSites(r.data.sites || [])); }, []);

  return (
    <>
      <div className="page-header">
        <div><h2>Caisse</h2><p>Comptage et rapprochement du tiroir-caisse</p></div>
        <button className="btn btn-primary" onClick={() => setClosureOpen(true)}>
          Démarrer la clôture
        </button>
      </div>
      <div className="page-body">
        <div className="tabs" style={{marginBottom:20}}>
          <div className={`tab${tab==='count'?' active':''}`} onClick={() => setTab('count')}>Comptage du jour</div>
          <div className={`tab${tab==='manual'?' active':''}`} onClick={() => setTab('manual')}>Entrées manuelles</div>
          <div className={`tab${tab==='safe'?' active':''}`} onClick={() => setTab('safe')}>Historique coffre</div>
          <div className={`tab${tab==='history'?' active':''}`} onClick={() => setTab('history')}>
            <History size={13} style={{display:'inline',marginRight:5}}/>Historique
          </div>
        </div>
        {tab==='count'   && can('cash.view') && <CountTab   sites={sites}/>}
        {tab==='history' && can('cash.view') && <HistoryTab sites={sites}/>}
        {tab==='manual'  && can('cash.view') && <ManualEntryTab sites={sites} can={can}/>}
        {tab==='safe'    && can('cash.view') && <SafeHistory/>}
        {!can('cash.view') && <div className="empty-state card"><p>Accès non autorisé</p></div>}
      </div>
      {closureOpen && <ClosureFlow onClose={() => setClosureOpen(false)}/>}
    </>
  );
}
